import { readLocale } from '@noor/api';
import {
  deliveryPricingResponseSchema,
  deliveryPricingWriteResponseSchema,
  localizedRestaurantWithScheduleResponseSchema,
  publishedRestaurantCardResponseSchema,
  restaurantSchema,
  restaurantWriteResponseSchema,
  type DaySchedule,
  type DeliveryPricing,
  type DeliveryPricingWriteResult,
  type ListResult,
  type LocalizedRestaurant,
  type LocalizedText,
  type MerchantModerationState,
  type PaginationParams,
  type PendingChangeRequest,
  type PublishedRestaurantCardResponse,
  type Restaurant,
  type RestaurantWriteResult,
  type WeeklySchedule,
} from '../model';
import {
  deleteOne,
  getList,
  getOne,
  getOneWithList,
  patchOneWithList,
  postOneWithList,
  postVoid,
  putOne,
  type RequestOptions,
} from './http';

// Restaurant Service. A "restaurant" is one branch of a merchant, so these are
// the branch-management endpoints: profile, open/close, delivery pricing and
// the weekly schedule.
//
// The merchant these are scoped to comes from the caller's JWT — `merchant_id`
// used to be passed explicitly (marked TEMP in the spec) until the auth service
// began issuing it as a claim.

/** Filters for `GET /merchant/restaurants/published` — branches visible to clients. */
export type ListPublishedRestaurantsParams = PaginationParams;

/**
 * Filters for `GET /merchant/restaurants`.
 *
 * No `include_hidden` knob: the merchant-scoped listing ALWAYS includes hidden
 * branches — a merchant browsing its own branches needs the unpublished ones
 * too. That flag belongs to the client-facing `GET /client/restaurants`.
 */
export type ListRestaurantsParams = PaginationParams & {
  name?: string;
  restaurant_category_id?: string;
  /**
   * Moderation state of the branch — the «Заведение» screen's status select,
   * sent as `state` in the query params.
   */
  state?: MerchantModerationState;
};

/**
 * `PATCH /merchant/restaurants/{id}` body — every field optional (a partial update).
 *
 * As of v1.00.158 the write body speaks TRANSLATIONS: `name`, `description`
 * and the address `line` are `v1LocalizedText`, not plain strings — sending a
 * string fails the whole request with `400 failed to encode payload` (verified
 * live). `week_schedule` replaces the removed `PUT .../working-hours`
 * endpoint: exactly 7 entries, one per day, replacing the stored schedule.
 *
 * NO `icon` / `background_image`: both were removed from this body when photos
 * became slot-managed (2026-08-07), and the gateway REJECTS unknown fields
 * (`400 unknown field "icon"`) rather than ignoring them — so leaving them here
 * would fail the whole branch update. Photos go through the Image Service —
 * {@link imageApi.upsert} / {@link imageApi.remove}, entity type `restaurant`.
 */
export interface UpdateRestaurantInput {
  pickup_available?: boolean;
  cuisine_ids?: { ids: string[] };
  name?: LocalizedText;
  address?: { line: LocalizedText; latitude: number; longitude: number };
  working_hours?: { open_time: string; close_time: string };
  menu_source?: string;
  external_id?: string;
  free_delivery_threshold?: number;
  /** So'm. A number sets it, `null` clears it, absent leaves it untouched. */
  min_order_amount?: number | null;
  description?: LocalizedText;
  phone?: string;
  delivery_provider?: string;
  /** RestaurantCategory rubric ids assigned to this branch. */
  restaurant_category_ids?: string[];
  /** Minutes; absent = unchanged, 0 = clear back to "not set". */
  min_prep_time?: number;
  max_prep_time?: number;
  /** Exactly 7 `v1DaySchedule` entries; replaces the stored per-day schedule. */
  week_schedule?: DaySchedule[];
}

/**
 * `POST /merchant/restaurants` body (`v1CreateRestaurantRequest`) — the create
 * counterpart of {@link UpdateRestaurantInput}.
 *
 * Two deliberate differences from the PATCH body:
 *
 * - `icon` / `background_image` ARE accepted here. Slot-managed photos replaced
 *   them on update, but a branch that does not exist yet has no id to address a
 *   slot with, so the create body still carries both as freshly-uploaded
 *   storage KEYS (never display URLs — see the app's CLAUDE.md #13).
 * - `name` and `address` are required; everything else is optional. Like the
 *   update body this speaks TRANSLATIONS — `name`, `description` and the
 *   address `line` are `v1LocalizedText`, and sending a bare string fails the
 *   whole request with `400 failed to encode payload`.
 */
export interface CreateRestaurantInput {
  pickup_available?: boolean;
  cuisine_ids?: string[];
  name: LocalizedText;
  address: { line: LocalizedText; latitude: number; longitude: number };
  working_hours?: { open_time: string; close_time: string };
  /** Freshly-uploaded storage key for the branch logo. */
  icon?: string;
  /** Freshly-uploaded storage key for the wide header photo. */
  background_image?: string;
  menu_source?: string;
  external_id?: string;
  free_delivery_threshold?: number;
  /** So'm; omit for no minimum order. */
  min_order_amount?: number;
  description?: LocalizedText;
  phone?: string;
  delivery_provider?: string;
  /** RestaurantCategory rubric ids assigned to this branch. */
  restaurant_category_ids?: string[];
  /** Minutes; 0 = "not set". */
  min_prep_time?: number;
  max_prep_time?: number;
  /** Exactly 7 `v1DaySchedule` entries — the branch's opening schedule. */
  week_schedule?: DaySchedule[];
}

/**
 * What a schedule write actually did — the same applied/pending split as the
 * product writes: a gated merchant's PATCH is queued behind moderation and
 * answers with a receipt instead of the saved schedule.
 */
export type ScheduleWriteOutcome =
  | { status: 'applied'; schedule: WeeklySchedule }
  | { status: 'pending'; pendingChange?: PendingChangeRequest };

// A branch's two photo slots used to live here as `RESTAURANT_IMAGE_SLOTS`,
// written through `POST/DELETE restaurants/{id}/image[/{slot}]`. Both routes
// are GONE as of the 2026-08-14 spec revision — photos now write through the
// Image Service (`imageApi`), where the slots are `IMAGE_TYPES.icon` and
// `IMAGE_TYPES.background`.

/**
 * `PUT /merchant/restaurants/{id}/delivery-pricing` body. NO `base_fee`: the v1.00.158
 * body dropped it, and the gateway rejects unknown fields (verified live).
 */
export interface SetDeliveryPricingInput {
  free_distance_m?: number;
  price_per_km?: number;
}

/** Preserve the display helpers' resolved shape without using client-token routes. */
function resolveRestaurant(
  restaurant: LocalizedRestaurant,
  locale: keyof LocalizedText,
): Restaurant {
  const text = (value: LocalizedText) => value[locale] || value.ru || '';
  return restaurantSchema.parse({
    ...restaurant,
    name: text(restaurant.name),
    description: text(restaurant.description),
    ...(restaurant.address
      ? { address: { ...restaurant.address, line: text(restaurant.address.line) } }
      : {}),
  });
}

export const restaurantApi = {
  /**
   * List branches visible to clients — `GET /merchant/restaurants/published`.
   */
  listPublished(
    params?: ListPublishedRestaurantsParams,
    options?: RequestOptions,
  ): Promise<ListResult<Restaurant>> {
    return getList('merchant/restaurants/published', restaurantSchema, params, options);
  },

  /**
   * Published branch card — `GET /merchant/restaurants/{id}/published`.
   * Answers with `{ client_visible: true, restaurant }` or `{ client_visible: false, restaurant: null }`.
   */
  getPublished(
    id: string,
    options?: RequestOptions,
  ): Promise<PublishedRestaurantCardResponse> {
    return getOne(
      `merchant/restaurants/${id}/published`,
      publishedRestaurantCardResponseSchema,
      undefined,
      options,
    );
  },

  /**
   * List the merchant's own branches, hidden ones included. The merchant comes
   * from the JWT claim, so no id is sent — `GET /client/restaurants` is the
   * client-facing listing and would not surface unpublished branches.
   */
  list(
    params?: ListRestaurantsParams,
    options?: RequestOptions,
  ): Promise<ListResult<Restaurant>> {
    return getList('merchant/restaurants', restaurantSchema, params, options);
  },

  /**
   * Create a branch — `POST /merchant/restaurants`.
   *
   * Answers with the SAME envelope as the update (the localized restaurant
   * under `data.restaurant` plus the saved 7-day schedule as a sibling `list`),
   * and with the same moderation split: a gated merchant's create is queued and
   * comes back as a `pending_change` receipt with no restaurant. Callers must
   * therefore handle a missing id rather than assume one — {@link
   * restaurantWriteResponseSchema} models both.
   */
  create(input: CreateRestaurantInput): Promise<RestaurantWriteResult> {
    return postOneWithList('merchant/restaurants', input, restaurantWriteResponseSchema);
  },

  /**
   * Signal that a branch's content is complete and ready for admin review —
   * `POST /restaurants/{id}/submit-for-review`. Does NOT change the branch's
   * status by itself; it only puts the branch in front of a moderator.
   */
  submitForReview(id: string): Promise<void> {
    return postVoid(`restaurants/${id}/submit-for-review`, {});
  },

  /** Get a branch for display using the merchant read and the active locale. */
  async get(id: string, options?: RequestOptions): Promise<Restaurant> {
    const { restaurant } = await restaurantApi.getLocalizedWithSchedule(id, options);
    return resolveRestaurant(restaurant, options?.locale ?? readLocale());
  },

  /**
   * Get one branch with EVERY translation, plus its weekly schedule, in one
   * call — `GET /merchant/restaurants/{id}`.
   *
   * This is what seeds the branch editor. The display helper `get` resolves `name`,
   * `description` and the address line to the requested locale, so it
   * can fill only one of the form's three language inputs; this endpoint
   * answers with a `v1LocalizedRestaurant` carrying all of them.
   */
  async getLocalizedWithSchedule(
    id: string,
    options?: RequestOptions,
  ): Promise<{ restaurant: LocalizedRestaurant; schedule: WeeklySchedule }> {
    const { data, list } = await getOneWithList(
      `merchant/restaurants/${id}`,
      localizedRestaurantWithScheduleResponseSchema,
      undefined,
      options,
    );
    return { restaurant: data.restaurant, schedule: list };
  },

  /** Get a branch and schedule under the merchant token, resolving text for display. */
  async getWithSchedule(
    id: string,
    options?: RequestOptions,
  ): Promise<{ restaurant: Restaurant; schedule: WeeklySchedule }> {
    const { restaurant, schedule } = await restaurantApi.getLocalizedWithSchedule(
      id,
      options,
    );
    return {
      restaurant: resolveRestaurant(restaurant, options?.locale ?? readLocale()),
      schedule,
    };
  },

  /**
   * Update a branch's profile — `PATCH /merchant/restaurants/{id}`.
   *
   * Answers with EITHER the updated restaurant (plus the saved 7-day schedule
   * as the sibling `list`) or, when the merchant's writes are gated behind
   * moderation, only a `pending_change` receipt. Both are successes, so the
   * raw envelope is returned rather than unwrapped to a restaurant the caller
   * might not have received.
   *
   * NOTE: the old `POST .../open` / `POST .../close` endpoints are GONE from
   * the gateway (`Unknown path`, verified live) — branch status is
   * moderation-controlled now, so there is no client-side open/close.
   */
  update(id: string, input: UpdateRestaurantInput): Promise<RestaurantWriteResult> {
    return patchOneWithList(
      `merchant/restaurants/${id}`,
      input,
      restaurantWriteResponseSchema,
    );
  },

  /** Get a branch's delivery-fee formula. */
  async getDeliveryPricing(
    restaurantId: string,
    options?: RequestOptions,
  ): Promise<DeliveryPricing> {
    const { delivery_pricing } = await getOne(
      `merchant/restaurants/${restaurantId}/delivery-pricing`,
      deliveryPricingResponseSchema,
      undefined,
      options,
    );
    return delivery_pricing;
  },

  /**
   * Replace a branch's delivery-fee formula.
   *
   * The v1.00.158 body carries only `free_distance_m` and `price_per_km` — the
   * old `base_fee` is REJECTED as an unknown field (verified live). The
   * response's key is `pricing` (not the GET's `delivery_pricing`), and it is
   * absent when the write was gated behind moderation.
   */
  setDeliveryPricing(
    restaurantId: string,
    input: SetDeliveryPricingInput,
  ): Promise<DeliveryPricingWriteResult> {
    return putOne(
      `merchant/restaurants/${restaurantId}/delivery-pricing`,
      input,
      deliveryPricingWriteResponseSchema,
    );
  },

  /**
   * Replace a branch's weekly schedule.
   *
   * The dedicated `PUT .../working-hours` endpoint is GONE from the gateway
   * (404 `no Route matched`, verified live) — the schedule now writes through
   * `PATCH /merchant/restaurants/{id}`'s `week_schedule` field, whose response carries
   * the saved schedule back as the sibling `list`.
   *
   * KNOWN BACKEND LIMIT: on a moderated (post-launch) branch the gateway
   * currently REJECTS schedule changes outright — `400 invalid restaurant
   * input: schedule changes are not yet supported on a moderated (post-launch)
   * restaurant update` (code 4104, verified live 2026-08-12). That error is
   * surfaced to the caller; nothing client-side can work around it until the
   * backend supports moderated schedule edits.
   */
  async setWorkingHours(
    restaurantId: string,
    schedule: DaySchedule[],
  ): Promise<ScheduleWriteOutcome> {
    const { data, list } = await restaurantApi.update(restaurantId, {
      week_schedule: schedule,
    });
    if (!data.restaurant) {
      return {
        status: 'pending',
        ...(data.pending_change ? { pendingChange: data.pending_change } : {}),
      };
    }
    return { status: 'applied', schedule: list };
  },

  /**
   * Delete a branch — `DELETE /merchant/restaurants/{id}`.
   *
   * NOT in the checked-in `openapi.json` (v1.00.262), which stops at
   * `PATCH /merchant/restaurants/{id}`. The route nevertheless EXISTS on the gateway:
   * probed unauthenticated it answers `401 Missing bearer token`, while a
   * method the gateway does not route (`PUT /restaurants/{id}`) and a bogus
   * path both answer `404 Route Not Found` — so the 401 is the router
   * acknowledging the route before auth rejects the call. The spec dump is
   * simply behind the gateway here.
   *
   * Because the route is undocumented, its moderation behaviour is unverified:
   * like the other merchant writes it may be queued behind review rather than
   * removing the branch outright. It is typed `Promise<void>` for that reason —
   * any receipt the gateway answers with is deliberately not modelled, and the
   * caller re-reads the listing instead of assuming the row is gone.
   */
  delete(id: string): Promise<void> {
    return deleteOne(`merchant/restaurants/${id}`);
  },

  /** Remove the Delever integration credentials attached to a branch. */
  deleteCredentials(restaurantId: string): Promise<void> {
    return deleteOne(`credentials/${restaurantId}`);
  },
};
